import { ConfirmContext } from "@app/components/Confirm";
import { GroupSelectionTable } from "@app/components/groups/GroupSelectionTable";
import {
  MemberSelectionTable,
  type SearchMemberType,
} from "@app/components/members/MemberSelectionTable";
import type { GroupType } from "@app/types/groups";
import type { LightWorkspaceType } from "@app/types/user";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@dust-tt/sparkle";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

type MembersManagementType = "manual" | "group";

function isMembersManagementType(
  value: string
): value is MembersManagementType {
  return value === "manual" || value === "group";
}

interface RestrictedAccessBodyProps {
  isManual: boolean;
  scimEnabled: boolean;
  managementType: MembersManagementType;
  owner: LightWorkspaceType;
  selectedMembers: SearchMemberType[];
  selectedGroups: GroupType[];
  onManagementTypeChange: (managementType: MembersManagementType) => void;
  onMembersUpdated: (members: SearchMemberType[]) => void;
  onGroupsUpdated: (groups: GroupType[]) => void;
  initialMembers?: SearchMemberType[];
}

export function RestrictedAccessBody({
  isManual,
  scimEnabled,
  managementType,
  owner,
  selectedMembers,
  selectedGroups,
  onManagementTypeChange,
  onMembersUpdated,
  onGroupsUpdated,
  initialMembers,
}: RestrictedAccessBodyProps) {
  const { t } = useTranslation();
  const confirm = useContext(ConfirmContext);

  const selectedMemberIds = useMemo(
    () => new Set(selectedMembers.map((m) => m.sId)),
    [selectedMembers]
  );

  const selectedGroupIds = useMemo(
    () => new Set(selectedGroups.map((g) => g.sId)),
    [selectedGroups]
  );

  const handleMemberSelectionChange = (
    _ids: Set<string>,
    users: SearchMemberType[]
  ) => {
    onMembersUpdated(users);
  };

  const handleGroupSelectionChange = (
    _ids: Set<string>,
    groups: GroupType[]
  ) => {
    onGroupsUpdated(groups);
  };

  const handleManagementTypeChange = async (newManagementType: string) => {
    if (!isMembersManagementType(newManagementType) || !scimEnabled) {
      return;
    }

    if (
      managementType === "manual" &&
      newManagementType === "group" &&
      selectedMembers.length > 0
    ) {
      const confirmed = await confirm({
        title: t("spaces.switchToGroupsTitle", "Switch to groups"),
        message: t(
          "spaces.switchToGroupAccessWarning",
          "This switches from manual member to group-based access. Your current member list will be saved but no longer active."
        ),
        validateLabel: t("spaces.switchAccessConfirmButton", "Confirm"),
        validateVariant: "primary",
      });

      if (confirmed) {
        onManagementTypeChange("group");
      }
    } else if (
      managementType === "group" &&
      newManagementType === "manual" &&
      selectedGroups.length > 0
    ) {
      const confirmed = await confirm({
        title: t("spaces.switchToMembersTitle", "Switch to members"),
        message: t(
          "spaces.switchToManualAccessWarning",
          "This switches from group-based access to manual member management. Your current group settings will be saved but no longer active."
        ),
        validateLabel: t("spaces.switchAccessConfirmButton", "Confirm"),
        validateVariant: "primary",
      });

      if (confirmed) {
        onManagementTypeChange("manual");
      }
    } else {
      onManagementTypeChange(newManagementType);
    }
  };

  return (
    <>
      {scimEnabled && (
        <div className="flex flex-row items-center justify-between">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                isSelect
                label={
                  managementType === "manual"
                    ? t("spaces.manualAccessLabel", "Manual access")
                    : t(
                        "spaces.provisionedGroupAccessLabel",
                        "Provisioned group access"
                      )
                }
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                label={t("spaces.manualAccessLabel", "Manual access")}
                onClick={() => {
                  void handleManagementTypeChange("manual");
                }}
              />
              <DropdownMenuItem
                label={t(
                  "spaces.provisionedGroupAccessLabel",
                  "Provisioned group access"
                )}
                onClick={() => {
                  void handleManagementTypeChange("group");
                }}
              />
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {isManual && (
        <MemberSelectionTable
          owner={owner}
          selectedMemberIds={selectedMemberIds}
          onSelectionChange={handleMemberSelectionChange}
          initialMembers={initialMembers}
        />
      )}

      {!isManual && (
        <GroupSelectionTable
          owner={owner}
          selectedGroupIds={selectedGroupIds}
          onSelectionChange={handleGroupSelectionChange}
        />
      )}
    </>
  );
}
